'use client'

import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

// Minimal dropdown menu (click-outside to close) — ui.jsx:197-237.
// Hand-rolled to match the prototype; no focus trap by design.
export interface RMenuItem {
  /** Leading icon node (size ~17 to match the prototype). */
  icon?: React.ReactNode
  label: React.ReactNode
  sub?: React.ReactNode
  count?: number
  active?: boolean
  onClick?: () => void
  danger?: boolean
}

export interface RMenuProps {
  /** Render-prop trigger: `(open, toggle) => node`. */
  button: (open: boolean, toggle: () => void) => React.ReactNode
  items: RMenuItem[]
  align?: 'left' | 'right'
  width?: number
  className?: string
}

export function RMenu({ button, items, align = 'left', width = 260, className }: RMenuProps) {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  return (
    <div ref={ref} className={cn('relative', className)}>
      {button(open, () => setOpen((o) => !o))}
      {open && (
        <div
          className={cn(
            'absolute top-[calc(100%+6px)] z-[60] max-h-[320px] overflow-y-auto animate-pop-in rounded-shape-md border border-outline bg-surface p-[6px] shadow-e3',
            align === 'right' ? 'right-0' : 'left-0'
          )}
          style={{ width }}
        >
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setOpen(false)
                it.onClick?.()
              }}
              className={cn(
                'flex w-full items-center gap-[10px] rounded-shape-xs px-[10px] py-[9px] text-left text-[13.5px] hover:bg-surface-low',
                it.active ? 'bg-surface-low font-bold' : 'bg-transparent font-medium',
                it.danger ? 'text-primary' : 'text-ink'
              )}
            >
              {it.icon ? <span className={cn('shrink-0', !it.danger && 'text-ink-secondary')}>{it.icon}</span> : null}
              <span className="min-w-0 flex-1">
                <span className="block">{it.label}</span>
                {it.sub ? <span className="block text-[12px] font-normal text-ink-muted">{it.sub}</span> : null}
              </span>
              {it.count != null ? (
                <span className={cn('font-mono text-[11px] font-bold', it.count ? 'text-primary' : 'text-ink-muted')}>{it.count}</span>
              ) : null}
              {it.active ? <Check size={16} className="shrink-0 text-primary" /> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
